import React, { useCallback, useEffect, useState } from "react";
import { PaymentsGrowCustomer } from "../../../Contracts/Customer";
import Table, { Column, DataSourceItem } from "../../../Components/Atoms/Table";
import Tag from "../../../Components/Atoms/Tag";
import { createSortFunction } from "../../../Utils/TableUtils";
import { useDebounce } from "../../../Utils/ActionUtils";
import { useCustomerStore } from "../../../Store/Customer";
import { useAuth0 } from "@auth0/auth0-react";
import LoadingPage from "../../LoadingPage";
import TablePaginationFooter from "../../../Components/Atoms/Table/TablePaginationFooter";
import { ReactComponent as CloseIcon } from "../../../Assets/ActionIcons/CloseIcon.svg";
import { ReactComponent as AllClientsSyncedIcon } from "../../../Assets/CustomerListPage/AllClientsSynced.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/ActionIcons/InfoIcon.svg";
import { useOnboardingStore } from "../../../Store/Onboarding";
import { capitalizeAfterSpace } from "../../../Contracts/Common";
import { useSettingsStore } from "../../../Store/Settings";
import { useTeamStore } from "../../../Store/Team";
import { PaymentRoutes } from "../../../Contracts/Routes";
import SyncUnmappedCustomerModal from "./SyncUnmappedCustomerModal";
import UnmappedCustomersTransactionsListModal from "./UnmappedCustomersTransactionsListModal";
import { PAGINATION_KEYS, usePagination } from "../../../Store/Pagination";

const UnmappedCustomersList: React.FC = () => {
  const [sortParamValue, setSortParamValue] = useState<string>("");
  const { adyenKYCDetails } = useOnboardingStore();
  const settings = useSettingsStore(state => state.settings);
  const getSettings = useSettingsStore(state => state.getSettings);
  const { getCurrentTeam, currentTeam } = useTeamStore();

  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.UNMAPPED_CUSTOMERS_LIST,
    { limit: 25, page: 1 }
  );

  const [showingTransactionListFor, setShowingTransactionListFor] = useState<number | undefined>();
  const [syncUnmappedCustomerModalProps, setSyncUnmappedCustomerModalProps] = useState<{
    isOpen: boolean;
    selectedCustomer?: PaymentsGrowCustomer;
  }>({
    isOpen: false
  });
  const columns: Column<PaymentsGrowCustomer>[] = [
    {
      title: "Client Name",
      dataIndex: "name",
      key: "name",
      sortClicked: createSortFunction("customer_name", setSortParamValue),
      width: "w-80",
      render: d => (
        <div
          className="text-blue-600 cursor-pointer"
          onClick={() => setShowingTransactionListFor(d.data.id)}
        >
          {d.name}
        </div>
      )
    },
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      width: "w-80",
      sortClicked: createSortFunction("any_user_email", setSortParamValue)
    },
    {
      title: `Sync to ${capitalizeAfterSpace(adyenKYCDetails?.connected_integration)}`,
      dataIndex: "sync",
      key: "sync",
      render: d => (
        <>
          {d.data.customer_id ? (
            <Tag color="lime">Synced</Tag>
          ) : (
            <button
              className="text-blue-600"
              onClick={() =>
                setSyncUnmappedCustomerModalProps({
                  isOpen: true,
                  selectedCustomer: d.data
                })
              }
            >
              Sync Client
            </button>
          )}
        </>
      )
    }
  ];

  if (!currentTeam?.early_access_features?.["customer-auto-mapping"]) {
    columns.push({
      title: "Unmap Grow Client",
      dataIndex: "unmap",
      key: "unmap",
      render: d => (
        <>
          {!d.data.customer_id ? (
            <div className="border rounded w-fit py-0.5 px-2 border-lime-300 bg-lime-100 text-lime-600">
              Not Synced
            </div>
          ) : (
            <button className="text-blue-600" onClick={() => unMapCustomerClicked(d.data)}>
              Unmap
            </button>
          )}
        </>
      )
    });
  }
  const [search, setSearch] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(search, 1500);
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setSearch(event.target.value);
      setPage(1);
    } else {
      setSearch(undefined);
      setPage(1);
    }
  };
  const { isLoading, growCustomers, growCustomersCount, getGrowCustomers, unmapGrowCustomer } =
    useCustomerStore();

  const { getAccessTokenSilently } = useAuth0();
  const refreshThings = useCallback(async () => {
    await getGrowCustomers(
      getAccessTokenSilently,
      limit,
      sortParamValue,
      page,
      undefined,
      debouncedSearch
    );
  }, [debouncedSearch, getGrowCustomers, getAccessTokenSilently, limit, sortParamValue, page]);
  useEffect(() => {
    refreshThings();
  }, [debouncedSearch, refreshThings, limit, sortParamValue, page]);

  useEffect(() => {
    const loadConfig = async () => {
      await Promise.all([
        getSettings(getAccessTokenSilently),
        getCurrentTeam(getAccessTokenSilently)
      ]);
    };
    loadConfig();
  }, [getSettings, getCurrentTeam, getAccessTokenSilently]);

  const unMapCustomerClicked = async (customer: PaymentsGrowCustomer) => {
    if (
      !window.confirm(
        `Are you sure you want to unmap the client ${customer.client_company_name || customer.customer_name}?`
      )
    ) {
      return;
    }
    try {
      await unmapGrowCustomer(getAccessTokenSilently, customer.id);
      refreshThings();
    } catch (err: any) {
      window.alert(err?.message || String(err));
    }
  };

  const dataSource: DataSourceItem<PaymentsGrowCustomer>[] = growCustomers.map(customer => ({
    key: customer.id.toString(),
    name: customer.client_company_name || customer.customer_name,
    user_email: customer.user_email,
    data: customer
  }));

  return (
    <>
      <SyncUnmappedCustomerModal
        {...syncUnmappedCustomerModalProps}
        onClose={() =>
          setSyncUnmappedCustomerModalProps({
            isOpen: false,
            selectedCustomer: undefined
          })
        }
      />
      <UnmappedCustomersTransactionsListModal
        isOpen={showingTransactionListFor !== undefined}
        onClose={() => setShowingTransactionListFor(undefined)}
        customerId={showingTransactionListFor}
      />
      {!isLoading && growCustomers.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center flex-grow">
          <AllClientsSyncedIcon />
          <h2 className="text-lg font-semibold mt-4">All Clients Are Synced</h2>
          <p className="text-gray-600 mt-2">You're all set! No unmapped clients found.</p>
        </div>
      ) : (
        <>
          {currentTeam?.early_access_features?.["customer-auto-mapping"] &&
            !settings?.client_creation_automated && (
              <div className="border border-blue-300 bg-blue-100 rounded p-2 my-2 flex items-center space-x-2">
                <InfoIcon className="w-5 h-5 text-blue-600" />
                <div>
                  Enable automated client creation under Settings to automatically create customers
                  in {capitalizeAfterSpace(adyenKYCDetails?.connected_integration)} and avoid manual
                  syncing.{" "}
                  <a
                    href={`${PaymentRoutes.SETTINGS_CONFIG}#branding`}
                    className="font-semibold cursor-pointer text-blue-600"
                  >
                    Configure Settings
                  </a>
                </div>
              </div>
            )}
          <div className="flex items-center space-x-2 mt-2">
            <input
              placeholder="Search for a customer.."
              value={search || ""}
              onChange={handleSearchInputChange}
              className="border border-gray-300 rounded p-1 my-1 w-48"
            />
            {search && (
              <CloseIcon
                className="h-3 w-3 hover:scale-110 transition-all duration-500 hover:text-red-600 cursor-pointer"
                onClick={() => setSearch(undefined)}
              />
            )}
          </div>
          <div className="flex flex-grow flex-col overflow-y-auto">
            {isLoading && growCustomers.length === 0 ? (
              <LoadingPage />
            ) : (
              <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
            )}
          </div>
          <TablePaginationFooter
            limit={limit}
            setLimit={setLimit}
            totalRecords={growCustomersCount}
            page={page}
            setPage={setPage}
            isLoading={isLoading}
          />
        </>
      )}
    </>
  );
};

export default UnmappedCustomersList;
